import { FormattedMessage, useIntl } from 'react-intl'
import FaqStructuredDataIntl from '~/components/SEO/FaqStructuredDataIntl'
import ProductDescription from '~/components/ProductDescription/ProductDescription'
import { ProductDescriptionDetails } from '~/components/ProductDescription/ProductDescriptionDetails'
import styles from './SeoContentSection.module.css'

type Props = {
  /**
   * Page key matching the `seo_content.<page>.*` locale namespace, e.g.
   * "configurator.wardrobe" or "catalog". Derived from the URL meta code in
   * main.tsx.
   */
  page: string
}

/**
 * Single, prerender-safe product/SEO content block.
 *
 * IMPORTANT: this is rendered in main.tsx OUTSIDE the route `<Suspense>`
 * boundary and BEFORE <Footer>. Every route is lazy-loaded, so
 * scripts/prerender.mjs snapshots the DOM while the route chunk is still
 * resolving (Suspense fallback) — anything inside a page component is NOT
 * captured in the static HTML. Rendering here is the only way to ship
 * crawlable text/headings on the pre-rendered configurator & catalog pages.
 *
 * Configurator pages render the real, shared "Despre produs" block
 * (<ProductDescription sections={ProductDescriptionDetails}>) — the same
 * component + locale keys used on /product/* pages, so there is a single
 * source of truth for that copy (materials, assembly, delivery, 24-month
 * warranty). To avoid duplication, the configurator page layout no longer
 * renders that block itself (see ProductPageLayout `showDescription`).
 *
 * The h1 is the page's commercial keyword; it lives here (not in the lazy
 * route) so every prerendered page has exactly one crawlable h1.
 */
export function SeoContentSection({ page }: Props) {
  const intl = useIntl()
  const base = `seo_content.${page}`
  const keys = Object.keys(intl.messages)
  const has = (k: string) =>
    Object.prototype.hasOwnProperty.call(intl.messages, k)

  // Nothing defined for this route -> render nothing.
  if (!has(`${base}.h1`)) return null

  const isConfigurator = page.startsWith('configurator.')

  const featurePrefix = `${base}.feature.`
  const featureKeys = keys
    .filter((k) => k.startsWith(featurePrefix))
    .sort(
      (a, b) =>
        Number(a.slice(featurePrefix.length)) -
        Number(b.slice(featurePrefix.length))
    )

  const faqPrefix = `${base}.faq.`
  const faqIndices = Array.from(
    new Set(
      keys
        .filter((k) => k.startsWith(faqPrefix) && k.endsWith('.q'))
        .map((k) => k.slice(faqPrefix.length, -'.q'.length))
    )
  ).sort((a, b) => Number(a) - Number(b))

  const faqItems = faqIndices.map((i) => ({
    title: `${faqPrefix}${i}.q`,
    content: `${faqPrefix}${i}.a`,
  }))

  return (
    <section className={styles.container} aria-labelledby={`${base}-h1`}>
      {faqItems.length > 0 && <FaqStructuredDataIntl items={faqItems} />}

      <h1 id={`${base}-h1`} className={styles.heading}>
        <FormattedMessage id={`${base}.h1`} />
      </h1>

      {has(`${base}.intro`) && (
        <p className={styles.intro}>
          <FormattedMessage id={`${base}.intro`} />
        </p>
      )}

      {featureKeys.length > 0 && (
        <ul className={styles.features}>
          {featureKeys.map((k) => (
            <li key={k}>
              <FormattedMessage id={k} />
            </li>
          ))}
        </ul>
      )}

      {isConfigurator && (
        <ProductDescription
          sections={ProductDescriptionDetails}
          heading="productPage.details.title"
          className={styles.description}
        />
      )}

      {faqItems.length > 0 && (
        <div className={styles.faq}>
          <h2 className={styles.faqTitle}>
            <FormattedMessage id={`${base}.faq_title`} />
          </h2>
          {faqItems.map((item) => (
            <div key={item.title} className={styles.faqItem}>
              <h3 className={styles.faqQuestion}>
                <FormattedMessage id={item.title} />
              </h3>
              <p className={styles.faqAnswer}>
                <FormattedMessage id={item.content} />
              </p>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
